/**
 * ColumnAnnotationChips — small per-column callouts ("FK ×N", "VIEW ×N",
 * "SEQ <name>") rendered next to the column name in `ColumnsTab`. Slice H2.
 *
 * Annotation sources:
 *   - **Sequence** chip: pure derivation from `defaultText` via
 *     `sequenceDefaultFromText` (always-on; no IPC needed).
 *   - **Incoming-FK** + **view-projection** chips: come from the dependency-
 *     annotation IPC via `useDependencyAnnotations`. Engines whose dependency
 *     service does not populate `columnName` (e.g., mssql today) leave these
 *     empty — the introspection-fallback's documented limit; NavFeatures
 *     Phase 2 (#11) is the upgrade path.
 */
import { ArrowDownToLine, Eye } from 'lucide-react';

import { getColumnAnnotation } from '../../dependencies/buildDependencyAnnotations';
import { sequenceDefaultFromText } from '../../dependencies/sequenceDefaultFromText';
import type { DependencyAnnotations } from '../../dependencies/types';

export interface ColumnAnnotationChipsProps {
  readonly columnName: string;
  readonly defaultText: string | null;
  readonly annotations: DependencyAnnotations | null;
  /** Required `data-testid` prefix — chip testIds become
   *  `${testId}-{kind}-chip-${columnName}`. */
  readonly testId: string;
}

export function ColumnAnnotationChips({
  columnName,
  defaultText,
  annotations,
  testId,
}: ColumnAnnotationChipsProps) {
  const ann = getColumnAnnotation(annotations, columnName);
  const seq = sequenceDefaultFromText(defaultText);
  const incomingFkCount = ann.incomingFkSources.length;
  const viewCount = ann.viewProjections.length;
  const hasAny = seq != null || incomingFkCount > 0 || viewCount > 0;
  if (!hasAny) return null;

  return (
    <span className="ml-2 inline-flex items-center gap-1 align-middle">
      {seq ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400"
          data-testid={`${testId}-sequence-chip-${columnName}`}
          title={`Default: ${seq.idiom === 'nextval' ? 'nextval' : 'NEXT VALUE FOR'} ${seq.sequenceName}`}
        >
          SEQ
        </span>
      ) : null}
      {incomingFkCount > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-sm bg-sky-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-400"
          data-testid={`${testId}-incoming-fk-chip-${columnName}`}
          data-count={incomingFkCount}
          title={`Referenced by ${incomingFkCount} foreign key${incomingFkCount === 1 ? '' : 's'}`}
        >
          <ArrowDownToLine className="h-2.5 w-2.5" /> FK ×{incomingFkCount}
        </span>
      ) : null}
      {viewCount > 0 ? (
        <span
          className="inline-flex items-center gap-0.5 rounded-sm bg-violet-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-700 dark:text-violet-400"
          data-testid={`${testId}-view-chip-${columnName}`}
          data-count={viewCount}
          title={`Projected by ${viewCount} view${viewCount === 1 ? '' : 's'}`}
        >
          <Eye className="h-2.5 w-2.5" /> VIEW ×{viewCount}
        </span>
      ) : null}
    </span>
  );
}
